import Schema from '../Schema';
import Shipments from './injected/Shipments';
import Shippings from './Shipping';
import { Payment as Payments } from './Payment';
import PremiumPlans from './PremiumPlans';
import ShoppingDiscounts from './ShoppingDiscounts';
import bigdecimalUtil from '../../../../../../lib/util/bigdecimal';
import { WELCOMING_GIFT, SHIPMENT_TYPE } from '../../constants/shop';

const ShoppingCarts = new Schema({
  _id: String,
  free_shipping_threshold: Number,
  // カートから削除される時間
  expire_at: Date,

  // カート有効期限までの残り秒数
  expiration_seconds: Number,

  // カート全体の徴収するImport Feesを返す
  import_fees: Number,
  // 商品の合計金額 Number
  grand_total: Number,
  // ギフトラッピング料金
  gift_wrapping_unit_price: Number,
  // 商品の合計金額 Number 用途不明
  grand_total_for_cart_page: Number,
  order_subtotal: Number,
  subtotal: Number,
  final_subtotal: Number,
  premium_discountable_price_total: Number,
  product_subtotal: Number,
  // 割引が考慮された実際に課金される送料
  shipping_charge: Number,
  // カート内の商品を購入した場合に取得できるポイント数
  acquire_point: Number,
  reserved_point: Number,
  point: Number,
  sku_discount_price_total: Number,
  sku_premium_discount_price_total: Number,
  sku_premium_saving_price_total: Number,
  // 現在適用中のクーポン情報
  discount: {
    type: String,
    ref: ShoppingDiscounts
  },
  // ギフトラッピングの合計金額
  gift_wrapping_charge: Number,
  // クーポンだけによる割引金額を計算する
  discount_price_with_coupon: Number,
  // クーポンだけによる実際の割引金額を計算する
  discount_real_price_with_coupon: Number,
  // 利用可能なポイントの上限
  max_usable_tom_point: Number,
  shipments: [
    {
      type: String,
      ref: Shipments
    }
  ],
  premium: {
    // 決済時に適用されるプラン
    plan: {
      type: String,
      ref: PremiumPlans
    },
    // 入会時に適用されるプランタイプ
    plan_type: {
      type: String,
      default: 'first'
    },
    // 入会しようとしている場合true
    is_joining: Boolean,
    // P会員特典を獲得できるかどうか
    can_get_bonus: Boolean,
    contract_incentive_point: Number,
    // 現在の TOM Point 取得率 (購入内容に関係ないグローバルな値)
    acquired_point_rate: Number,
    // Premium 加入前条件で Premium クーポンが使用可能な場合のみ true になる
    coupon_is_available_before_premium_fund: Boolean
  },
  // recontract 時に得られる recontract_extra_point による割引額
  // 条件を満たさない場合 0
  premium_will_gain_recontract_extra_discount: Number,
  // recontract 時に得られる予定の recontract_extra_point による割引額
  premium_assumed_will_gain_recontract_extra_discount: Number,
  // カート決済で使用可能なP会員入会のボーナスポイント数
  premium_subscription_bonus_point: Number,
  wishlistId: String,
  wishlist_user: {
    avatar_image: String,
    name: String
  },
  can_use_point: Boolean,
  acquired_point_rate: Number,
  show_acquired_point_rate_notice: Boolean,
  unavailable_payment_methods: Array
});

ShoppingCarts.shipmentIndexWithoutDigital = (shipments, id) => {
  let i = 0;
  shipments.some((shipment) => {
    if (!shipment.has_digital_content) i++;
    if (shipment._id === id) return true;
  });
  return i;
};

ShoppingCarts.shippingFrom = (shipment) => {
  switch (shipment.items[0].sku.region) {
    case 'Maihama':
      return 'from Japan';
    case 'LA':
      return 'from the United States(Los Angeles)';
    case 'PDX':
      return 'from the United States';
    case 'Commerce':
      return 'from the United States';
  }
};

ShoppingCarts.premiumPlanPointPrice = (cart, user) => {
  if (user && !user.possible_premium_plan_type) return 0;
  return bigdecimalUtil.divide(
    PremiumPlans.findById(cart.premium.plan).type[
      user.possible_premium_plan_type
    ].point,
    100
  );
};

ShoppingCarts.welcomeDiscount = (cart, user) => {
  if (user && !user.possible_premium_plan_type) return 0;
  return Math.min(
    ShoppingCarts.premiumPlanPointPrice(cart, user),
    cart.subtotal
  );
};

ShoppingCarts.premiumJoiningSavePrice = (cart, user) => {
  if (user && !user.possible_premium_plan_type) return 0;
  const recontractExtraDiscount =
    cart.premium_assumed_will_gain_recontract_extra_discount || 0;
  const premiumDiscountPrice = bigdecimalUtil.add(
    ShoppingCarts.premiumPlanPointPrice(cart, user),
    recontractExtraDiscount
  );
  const discount = bigdecimalUtil.add(
    premiumDiscountPrice,
    cart.sku_premium_saving_price_total
  );
  return Math.min(
    discount,
    Math.max(cart.premium_discountable_price_total, premiumDiscountPrice)
  );
};

ShoppingCarts.orderSubtotalForCart = (cart, user) => {
  let orderSubtotal = cart.reserved_point
    ? bigdecimalUtil.subtract(cart.subtotal, cart.reserved_point)
    : cart.subtotal;

  if (!user.is_login || user.has_welcoming_gift_coupon) {
    orderSubtotal = bigdecimalUtil.subtract(
      orderSubtotal,
      WELCOMING_GIFT.WELCOMING_GIFT_AMOUNT_OFF
    );
  }

  if (user && user.will_join_premium && user.possible_premium_plan_type) {
    const welcomeDiscount = ShoppingCarts.welcomeDiscount(cart, user);
    const recontractExtraDiscount =
      cart.premium_will_gain_recontract_extra_discount || 0;
    orderSubtotal = bigdecimalUtil.add(
      bigdecimalUtil.subtract(
        orderSubtotal,
        welcomeDiscount,
        recontractExtraDiscount
      ),
      PremiumPlans.findById(cart.premium.plan).type[
        user.possible_premium_plan_type
      ].price.value
    );
  }

  // 価格が$0以下の場合は$0を返す
  orderSubtotal = Math.max(orderSubtotal, 0);
  return orderSubtotal;
};

ShoppingCarts.orderSubtotal = (cart, user) => {
  let subtotal = cart.final_subtotal;

  if (user && user.will_join_premium && user.possible_premium_plan_type) {
    const welcomeDiscount = ShoppingCarts.welcomeDiscount(cart, user);
    const recontractExtraDiscount =
      cart.premium_will_gain_recontract_extra_discount || 0;
    subtotal = bigdecimalUtil.add(
      bigdecimalUtil.subtract(
        subtotal,
        welcomeDiscount,
        recontractExtraDiscount
      ),
      PremiumPlans.findById(cart.premium.plan).type[
        user.possible_premium_plan_type
      ].price.value
    );
  }

  return subtotal;
};

// カートに商品がないか
ShoppingCarts.isEmpty = (cart) => {
  return !(cart && cart.shipments && cart.shipments.length > 0);
};

// カートから商品が削除される時間の取得
ShoppingCarts.getExpireAt = (cart) => {
  if (ShoppingCarts.isEmpty(cart)) {
    return null;
  }
  return cart.expire_at;
};

// カートが有効期限内かどうか
ShoppingCarts.isWithinExpiration = (cart) => {
  if (ShoppingCarts.isEmpty(cart)) {
    return false;
  }
  return cart.expiration_seconds > 0;
};

// wishList giftからの購入かどうか
ShoppingCarts.isGiftFromWishList = (cart) => {
  if (ShoppingCarts.isEmpty(cart)) {
    return false;
  }
  return !!cart.wishlistId;
};

// digital content giftの購入かどうか
ShoppingCarts.hasDigitalContentGift = (cart) => {
  if (ShoppingCarts.isEmpty(cart)) {
    return false;
  }
  return cart.shipments.some((shipment) => {
    return shipment.items.some((item) => {
      return (
        item.gift &&
        item.gift.receiver &&
        item.gift.receiver.name &&
        item.gift.receiver.email
      );
    });
  });
};

// 購入可能な状態かどうか
ShoppingCarts.canPurchase = (cart, user) => {
  const currentPayment = Payments.getCurrent();
  const currentShipping = Shippings.getCurrent();
  const shipments = cart.shipments || [];

  return (
    cart &&
    currentPayment &&
    currentShipping &&
    shipments.length &&
    !Shipments.hasUndeliverableItemInShipments(shipments) &&
    !(cart.premium.is_joining && user.has_waiting_auto_recurring_premium)
  );
};

// ユーザーが入会できるプレミアムプランが初回かどうか
ShoppingCarts.isPremiumFirstPlan = (user) => {
  return user.possible_premium_plan_type === 'first';
};

// ユーザーが入会できるプレミアムプランが再入会かどうか
ShoppingCarts.isPremiumRecontractPlan = (user) => {
  return user.possible_premium_plan_type === 'recontract';
};

ShoppingCarts.availablePoint = (cart, user) => {
  return user.point_account.point_to_dollar - cart.reserved_point;
};

// Pointでのみ購入できる商品のみか
ShoppingCarts.isAllItemPointOnlyPurchase = (cart) => {
  const items = [];
  cart.shipments.forEach((shipment) => {
    return shipment.items.forEach((item) => {
      items.push(item);
    });
  });
  return items.every((item) => item.sku.flags.point_only);
};

// Pointでのみ購入できる商品を含むか
ShoppingCarts.hasPointOnlyProduct = (cart) => {
  const items = [];
  cart.shipments.forEach((shipment) => {
    return shipment.items.forEach((item) => {
      items.push(item);
    });
  });
  return items.some((item) => item.sku.flags.point_only);
};

ShoppingCarts.contractIncentiveDiscount = (cart) => {
  if (!(cart.premium && cart.premium.contract_incentive_point > 0)) {
    return 0;
  }
  return bigdecimalUtil.divide(cart.premium_subscription_bonus_point, 100);
};

// デジコンのみの購入か
ShoppingCarts.isDigitalProductsOnly = (cart) => {
  return cart.shipments.every(
    (shipment) => shipment.shipment_type === SHIPMENT_TYPE.DIGITAL_PRODUCT
  );
};

export default ShoppingCarts;
